import React from 'react';
import { includes, isEqual, keys, map, size, uniq, values } from 'lodash';
import memoizeOne from 'memoize-one';
import PERFORMANCE_STATUS from 'modules/campaigns/config/performanceStatus';
import { updateLibraryCreatives } from 'modules/campaigns/flows';
import { bulkResubmitCreatives, deleteCreatives, updateCreatives } from 'modules/campaigns/flows';
import updateCreativesBase from 'modules/campaigns/flows/updateCreativesBase';
import { BULK_ACTION } from 'modules/campaigns/modules/campaigns-reports/components/BulkActionToolbar/bulkAction';
import {
    hasResubmittableData,
    isAppInstall,
    isCreativeResubmittable,
    isEditCreativeAllowed,
} from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import { bulkDuplicateCreative, bulkEditCreative } from 'modules/campaigns/modules/campaigns-reports/flows';
import {
    getIsEligibleForResubmission,
    resubmitModalPrompt,
    sendGtmResubmitEvent,
} from 'modules/campaigns/modules/campaigns-reports/hooks';
import { PAGE_SIZE } from 'modules/campaigns/modules/campaigns-reports/utils';
import { isCarouselCreative } from 'modules/campaigns/modules/creative-creator/components/CarouselCardList/utils/isCarouselCreative';
import { campaignsStateSelector } from 'modules/campaigns/selectors';
import { COMMON_FLAGS, FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { HISTORY_METHOD } from 'modules/taboola-common-frontend-modules/query-params';

const hasCarousel = memoizeOne(selectedRows => values(selectedRows).some(isCarouselCreative));
const hasNonLibraryCreatives = memoizeOne(selectedRows => values(selectedRows).some(row => !row.isCreativeLibrary));
const hasLibraryCreatives = memoizeOne(
    (selectedRows, isCreativeLibraryConfigEnabled) =>
        values(selectedRows).some(row => row.isCreativeLibrary) && isCreativeLibraryConfigEnabled === 'true'
);

const isActionClickable = (selectedRows, actionType) => {
    const adsTypes = uniq(map(values(selectedRows), 'performanceStatus'));

    return includes(adsTypes, PERFORMANCE_STATUS.HTML_CARD) && (size(adsTypes) > 1 || actionType === BULK_ACTION.EDIT);
};

export const getEditDisabledTooltip = memoizeOne((selectedRows, accountConfig = {}) => {
    const isCreativeLibraryConfigEnabled = accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED];
    const rows = values(selectedRows);
    const hasVideoAds = rows.some(({ performanceStatus }) => performanceStatus === PERFORMANCE_STATUS.MOTION);
    const hasImageAds = rows.some(({ performanceStatus }) => performanceStatus === PERFORMANCE_STATUS.IMAGE);
    if (hasVideoAds && hasImageAds) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="app.campaigns.reports.bulkAction.toolbar.edit.creatives.tooltip"
                    defaultMessage="Motion Ads can not be edited with Single Image Ads in bulk. Please select only one ad type."
                />
            ),
        };
    }
    if (hasLibraryCreatives(selectedRows, isCreativeLibraryConfigEnabled)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.creative.library"
                    defaultMessage="Creatives can be duplicated and edited in the creative library."
                />
            ),
        };
    }
    const hasUnresubmittableAds = rows.some(row => hasResubmittableData(row) && !isCreativeResubmittable(row));
    if (hasUnresubmittableAds) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="app.campaigns.reports.bulkAction.toolbar.edit.creatives.tooltip.resubmit"
                    defaultMessage="At least one ad on this list has reached the submission limit. Please remove the ad(s) from the list in order to try again. Before attempting to submit new ads, please be sure you have reviewed our <a>Advertiser Content Policies</a>."
                />
            ),
        };
    }
    const hasUneditableRows = rows.some(row => !isEditCreativeAllowed(row));
    if (hasUneditableRows) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="app.campaigns.reports.bulkAction.toolbar.edit.creatives.tooltip.general"
                    defaultMessage="Not all selected ads can be edited."
                />
            ),
        };
    }
    const isEligibleForEdit = isActionClickable(selectedRows, BULK_ACTION.EDIT);
    if (isEligibleForEdit) {
        return {
            disabled: true,
        };
    }
});
export const getDuplicateDisabledTooltip = memoizeOne((selectedRows, accountConfig = {}) => {
    const isCreativeLibraryConfigEnabled = accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED];
    if (hasCarousel(selectedRows)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.carousel"
                    defaultMessage="At least one carousel card selected, action is unavailable."
                />
            ),
        };
    }
    if (hasLibraryCreatives(selectedRows, isCreativeLibraryConfigEnabled)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.creative.library"
                    defaultMessage="Creatives can be duplicated and edited in the creative library."
                />
            ),
        };
    }
    if (values(selectedRows).some(isAppInstall)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.appInstall"
                    defaultMessage="At least one app install card selected, action is unavailable."
                />
            ),
        };
    }
    const isEligibleForDuplicate = isActionClickable(selectedRows, BULK_ACTION.DUPLICATE);
    if (isEligibleForDuplicate) {
        return {
            disabled: true,
        };
    }
});
export const getResubmitDisabledTooltip = memoizeOne((selectedRows, commonConfig = {}, accountConfig = {}) => {
    const isCreativeLibraryConfigEnabled = accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED];
    const rejectReasonsRaw = commonConfig[COMMON_FLAGS.RESUBMIT_CREATIVES_LANDING_PAGE_REJECTION_REASONS] || '';
    const eligibleRejectReasons = rejectReasonsRaw.split(',');

    const isEligibleForResubmission = getIsEligibleForResubmission(
        selectedRows,
        keys(selectedRows),
        eligibleRejectReasons
    );

    if (!isEligibleForResubmission || hasLibraryCreatives(selectedRows, isCreativeLibraryConfigEnabled)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.resubmit"
                    defaultMessage="Not all selected ads can be resubmitted."
                />
            ),
        };
    }
    if (hasCarousel(selectedRows)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.carousel"
                    defaultMessage="At least one carousel card selected, action is unavailable."
                />
            ),
        };
    }
});

export const getPlayPauseDisabledTooltip = memoizeOne((selectedRows, accountConfig = {}) => {
    const isCreativeLibraryConfigEnabled = accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED];

    if (hasCarousel(selectedRows)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.carousel"
                    defaultMessage="At least one carousel card selected, action is unavailable."
                />
            ),
        };
    }

    if (hasLibraryCreatives(selectedRows, isCreativeLibraryConfigEnabled) && hasNonLibraryCreatives(selectedRows)) {
        return {
            disabled: true,
        };
    }
});

export const getRemoveDisabledTooltip = memoizeOne((selectedRows, accountConfig = {}) => {
    const isCreativeLibraryConfigEnabled = accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED];
    if (hasCarousel(selectedRows)) {
        return {
            disabled: true,
            message: (
                <FormattedMessage
                    id="creative.bulk.button.disabled.tooltip.carousel"
                    defaultMessage="At least one carousel card selected, action is unavailable."
                />
            ),
        };
    }
    if (hasLibraryCreatives(selectedRows, isCreativeLibraryConfigEnabled) && hasNonLibraryCreatives(selectedRows)) {
        return {
            disabled: true,
        };
    }
});

const getRemoveModalMessages = selectedCount => ({
    title: (
        <FormattedMessage
            id="creative.remove.header"
            tagName="h2"
            defaultMessage="{selectedCount, plural, one{Remove Ad?} other{Remove Ads?}}"
            values={{ selectedCount }}
        />
    ),
    content: (
        <FormattedMessage
            id="app.modal.removeCreatives.content"
            defaultMessage="{selectedCount, plural, one{You are about to remove {selectedCount} from the campaign. Once an ad is removed, it can never run again. Instead of removing, you can pause the ad. Do you wish to proceed?} other{You are about to remove {selectedCount} from the campaign. Once ad are removed, they can never run again. Instead of removing, you can pause the ad. Do you wish to proceed?} ads}}"
            values={{ selectedCount }}
        />
    ),
    formProps: { submitButtonText: <FormattedMessage id="app.actionButtons.REMOVE" /> },
});

const generateSharedValues = selectedRows => {
    const creativeValues = Object.values(selectedRows).map(
        ({ title, description, url, cta, thumbnailUrl, videoUrl }) => ({
            title,
            description,
            url,
            cta,
            thumbnailUrl,
            videoUrl,
        })
    );
    return creativeValues.length
        ? creativeValues.reduce((prevSharedValues, creative) => {
              let sharedValues = prevSharedValues;
              Object.keys(prevSharedValues).forEach(key => {
                  if (!isEqual(prevSharedValues[key], creative[key])) {
                      const { [key]: _, ...rest } = sharedValues;
                      sharedValues = rest;
                  }
              });
              return sharedValues;
          })
        : {};
};

const handlePlayPauseCreatives = ({ accountName, selectedRows, accountConfig = {}, isActive }) => {
    const isCreativeLibraryConfigEnabled = accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED];
    return dispatch => {
        return dispatch(
            updateCreatives(
                accountName,
                keys(selectedRows),
                { isActive },
                {
                    updateFunction: hasLibraryCreatives(selectedRows, isCreativeLibraryConfigEnabled)
                        ? updateLibraryCreatives
                        : updateCreativesBase,
                }
            )
        );
    };
};

export const bulkActions = [
    {
        key: BULK_ACTION.PLAY,
        handler: ({ accountName, selectedRows, accountConfig }) =>
            handlePlayPauseCreatives({ accountName, selectedRows, accountConfig, isActive: true }),
        enableCondition: ({ selectedRows = {}, accountConfig = {} }) =>
            !getPlayPauseDisabledTooltip(selectedRows, accountConfig)?.disabled,
        getTooltip: ({ selectedRows = {}, accountConfig = {} }) =>
            getPlayPauseDisabledTooltip(selectedRows, accountConfig)?.message,
    },
    {
        key: BULK_ACTION.PAUSE,
        handler: ({ accountName, selectedRows, accountConfig }) =>
            handlePlayPauseCreatives({ accountName, selectedRows, accountConfig, isActive: false }),
        enableCondition: ({ selectedRows = {}, accountConfig = {} }) =>
            !getPlayPauseDisabledTooltip(selectedRows, accountConfig)?.disabled,
        getTooltip: ({ selectedRows = {}, accountConfig = {} }) =>
            getPlayPauseDisabledTooltip(selectedRows, accountConfig)?.message,
    },
    {
        key: BULK_ACTION.EDIT,
        handler:
            ({ selectedRows, accountName, navigate }) =>
            dispatch => {
                const bulkEditCreativesSharedValues = generateSharedValues(selectedRows);

                dispatch(
                    bulkEditCreative({
                        navigate,
                        accountId: accountName,
                        selectedRowsIds: keys(selectedRows),
                        bulkEditCreativesSharedValues,
                        selectedRows,
                    })
                );
            },
        enableCondition: ({ selectedRows = {}, accountConfig = {} }) =>
            !getEditDisabledTooltip(selectedRows, accountConfig)?.disabled,
        getTooltip: ({ selectedRows = {}, accountConfig = {} }) =>
            getEditDisabledTooltip(selectedRows, accountConfig)?.message,
        tooltipOptions: { interactive: true },
    },
    {
        key: BULK_ACTION.DUPLICATE,
        handler: ({ accountName, selectedRows, navigate }) =>
            bulkDuplicateCreative({
                navigate,
                accountId: accountName,
                selectedRowsIds: keys(selectedRows),
            }),
        enableCondition: ({ selectedRows = {}, accountConfig = {} }) =>
            !getDuplicateDisabledTooltip(selectedRows, accountConfig)?.disabled,
        getTooltip: ({ selectedRows = {}, accountConfig = {} }) =>
            getDuplicateDisabledTooltip(selectedRows, accountConfig)?.message,
    },
    {
        key: BULK_ACTION.REMOVE,
        getPrompt: ({ selectedRows }) => getRemoveModalMessages(size(selectedRows)),
        handler:
            ({ selectedRows, accountName, forceFetchReport, reportPage, setReportPage, accountConfig = {} }) =>
            async (dispatch, getState) => {
                const { reportTotalRowsCount } = campaignsStateSelector(getState());
                const shouldArchiveAds = hasLibraryCreatives(
                    selectedRows,
                    accountConfig[FEATURE_FLAGS.CREATIVE_LIBRARY_ENABLED]
                );
                const selectedItems = keys(selectedRows).map(id => ({
                    creativeId: id,
                    adId: selectedRows[id].adId,
                }));
                await dispatch(deleteCreatives(accountName, selectedItems, shouldArchiveAds));
                const lastReportPage = Math.ceil((reportTotalRowsCount - size(selectedRows)) / PAGE_SIZE);
                const newReportPage = Math.min(reportPage, lastReportPage);
                if (newReportPage !== 1 && newReportPage !== reportPage) {
                    setReportPage(newReportPage, HISTORY_METHOD.REPLACE);
                    return;
                }
                forceFetchReport(Date.now());
            },
        enableCondition: ({ selectedRows = {}, accountConfig = {} }) =>
            !getRemoveDisabledTooltip(selectedRows, accountConfig)?.disabled,
        getTooltip: ({ selectedRows = {}, accountConfig = {} }) =>
            getRemoveDisabledTooltip(selectedRows, accountConfig)?.message,
    },
    {
        key: BULK_ACTION.RESUBMIT,
        handler:
            ({ selectedRows, accountName }) =>
            dispatch => {
                const creativeIds = keys(selectedRows);
                sendGtmResubmitEvent(creativeIds.length);
                dispatch(bulkResubmitCreatives({ creativeIds, accountId: accountName }));
            },
        accountConfigurations: {
            [FEATURE_FLAGS.CREATIVE_RESUBMISSION_ENABLED]: 'true',
            [FEATURE_FLAGS.SUBMISSION_DATA_ALLOWED_IN_ADS_REPORT]: 'true',
        },
        getPrompt: () => resubmitModalPrompt,
        enableCondition: ({ selectedRows, commonConfig, accountConfig = {} }) =>
            !getResubmitDisabledTooltip(selectedRows, commonConfig, accountConfig)?.disabled,
        getTooltip: ({ selectedRows, commonConfig, accountConfig = {} }) =>
            getResubmitDisabledTooltip(selectedRows, commonConfig, accountConfig)?.message,
    },
];
